import { useEffect, useState, useCallback } from 'react';

/**
 * Hook to detect unsaved changes in forms
 * Tracks dirty state by monitoring form inputs, textareas, and contenteditable elements
 */
export const useUnsavedChanges = () => {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  // Check for dirty forms on the page
  const checkForDirtyForms = useCallback(() => {
    // Check for form elements with dirty state
    const forms = document.querySelectorAll('form');
    let foundDirty = false;

    forms.forEach((form) => {
      // Check if form has data-dirty attribute
      if (
        form.hasAttribute('data-dirty') &&
        form.getAttribute('data-dirty') === 'true'
      ) {
        foundDirty = true;
        return;
      }

      // Check for inputs, textareas, and selects that have been modified
      const inputs = form.querySelectorAll('input, textarea, select');
      inputs.forEach((input) => {
        if (
          input instanceof HTMLInputElement ||
          input instanceof HTMLTextAreaElement ||
          input instanceof HTMLSelectElement
        ) {
          // Check if input has been modified (value differs from default)
          let defaultValue: string | null =
            input.getAttribute('data-default-value');
          if (!defaultValue) {
            if (
              input instanceof HTMLInputElement ||
              input instanceof HTMLTextAreaElement
            ) {
              defaultValue = input.defaultValue;
            } else if (input instanceof HTMLSelectElement) {
              defaultValue = input.options[input.selectedIndex]?.value || '';
            }
          }
          if (input.value !== defaultValue) {
            foundDirty = true;
          }
        }
      });
    });

    // Check for contenteditable elements with changes
    const contentEditables = document.querySelectorAll(
      '[contenteditable="true"]'
    );
    contentEditables.forEach((el) => {
      if (
        el.hasAttribute('data-dirty') &&
        el.getAttribute('data-dirty') === 'true'
      ) {
        foundDirty = true;
      }
    });

    // Check for React Hook Form dirty state (if available)
    const reactHookForms = document.querySelectorAll('[data-rhf-dirty="true"]');
    if (reactHookForms.length > 0) {
      foundDirty = true;
    }

    setIsDirty(foundDirty);
    setHasUnsavedChanges(foundDirty);
  }, []);

  // Monitor for changes
  useEffect(() => {
    // Check initially
    checkForDirtyForms();

    // Set up mutation observer to watch for DOM changes
    const observer = new MutationObserver(() => {
      checkForDirtyForms();
    });

    // Observe the entire document for changes
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-dirty', 'data-rhf-dirty'],
      childList: true,
      subtree: true,
    });

    // Also listen for input events
    const handleInput = () => {
      checkForDirtyForms();
    };

    document.addEventListener('input', handleInput);
    document.addEventListener('change', handleInput);

    // Check periodically as a fallback
    const interval = setInterval(checkForDirtyForms, 1000);

    return () => {
      observer.disconnect();
      document.removeEventListener('input', handleInput);
      document.removeEventListener('change', handleInput);
      clearInterval(interval);
    };
  }, [checkForDirtyForms]);

  return { hasUnsavedChanges, isDirty };
};
